// Purpose: Hook for fetching and updating a user's profile record from Supabase.

import { useState, useCallback } from 'react';
import { User } from '@supabase/supabase-js';
import { toast as sonnerToast } from "sonner";
import { ProfilesAPI } from '@/api';
import type { Profile } from '@/types/database.types';

// Re-export Profile type for convenience
export type { Profile } from '@/types/database.types';

export const useProfile = () => {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUserProfile = useCallback(async (userId: string) => {
    setIsLoading(true);
    try {
      const response = await ProfilesAPI.fetchProfile(userId);

      if (!response.success) {
        console.error('Error fetching user profile:', response.error);
      } else if (response.data) {
        setProfile(response.data);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateProfile = useCallback(async (data: Partial<Profile>, user: User | null) => {
    if (!user) return;
    try {
      const response = await ProfilesAPI.updateProfile(user.id, data);

      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to update profile');
      }

      setProfile(prev => prev ? { ...prev, ...data } : null);

      sonnerToast.success("Profile updated successfully");
    } catch (error: any) {
      sonnerToast.error("Error updating profile", {
        description: error.message
      });
      throw error;
    }
  }, []);

  const uploadAvatar = useCallback(async (file: File, user: User | null) => {
    if (!user) return null;
    
    // Validate file size (max 10MB)
    const MAX_FILE_SIZE = 10 * 1024 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      sonnerToast.error('File too large', {
        description: 'Maximum file size is 10MB'
      });
      return null;
    }

    try {
      const response = await ProfilesAPI.uploadAvatar(user.id, file);

      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to upload avatar');
      }

      const avatarUrl = response.data?.avatarUrl || null;
      setProfile(prev => prev ? { ...prev, avatar_url: avatarUrl } : null);

      sonnerToast.success('Profile picture updated');
      return avatarUrl;
    } catch (error: any) {
      sonnerToast.error('Error uploading profile picture', {
        description: error.message
      });
      return null;
    }
  }, []);

  return {
    profile,
    isLoading,
    setProfile,
    fetchUserProfile,
    updateProfile,
    uploadAvatar,
  };
};
